import { useMutation, useQueryClient } from "@tanstack/react-query";
import { familyService } from "@/services/familyService";
import { toast } from "@/hooks/use-toast";
import type { FamilyData } from "@/hooks/useOptimizedFamily";
import {
  addFamilyMemberWithEncryptedPan,
  createFamilyGoalForFamily,
  createFamilyGroupWithPan,
  requireFamilyId,
  requireUserAndFamily,
  requireUserId,
  resolveAddFamilyMemberToastMessage,
  toastErrorMessage,
  updateFamilyMemberWithOptionalPanEncryption,
  type FamilyGoalInput,
  type FamilyMemberInput,
  type FamilyMemberUpdate,
  type PrimaryMemberInput,
} from "@/hooks/useFamilyMutationsHelpers";
import { toastFamilyCreated } from "@/hooks/useOptimizedFamilyViewHelpers";

/**
 * Use Family Mutations.
 */
export function useFamilyMutations(options: {
  userId: string | undefined;
  familyData: FamilyData | undefined;
}) {
  const { userId, familyData } = options;
  const queryClient = useQueryClient();

  const invalidateFamily = () => {
    queryClient.invalidateQueries({ queryKey: ["family", userId] });
    // FamilyGoalsList keeps its own query key — must refresh after goal mutations (QA Bug #9).
    queryClient.invalidateQueries({ queryKey: ["family", "goals-list"] });
    queryClient.invalidateQueries({ queryKey: ["family", "goal-contributions"] });
    queryClient.invalidateQueries({ queryKey: ["goals"] });
  };

  const createFamilyMutation = useMutation({
    mutationFn: ({
      familyName,
      primaryMember,
    }: {
      familyName: string;
      primaryMember: PrimaryMemberInput;
    }) => createFamilyGroupWithPan(requireUserId(userId), familyName, primaryMember),
    onSuccess: (result) => {
      invalidateFamily();
      toast(toastFamilyCreated(result.panEncryptionSkipped));
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: toastErrorMessage(error, "Failed to create family"),
        variant: "destructive",
      });
    },
  });

  const createFamilyGoalMutation = useMutation({
    mutationFn: (goalData: FamilyGoalInput) => {
      const { familyId } = requireUserAndFamily(userId, familyData?.family?.id);
      return createFamilyGoalForFamily(familyId, goalData);
    },
    onSuccess: () => {
      invalidateFamily();
      toast({ title: "Success", description: "Family goal created successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: toastErrorMessage(error, "Failed to create family goal"),
        variant: "destructive",
      });
    },
  });

  const addFamilyMemberMutation = useMutation({
    mutationFn: (memberData: FamilyMemberInput) => {
      const ids = requireUserAndFamily(userId, familyData?.family?.id);
      return addFamilyMemberWithEncryptedPan(ids.familyId, ids.userId, memberData);
    },
    onSuccess: () => {
      invalidateFamily();
      toast({ title: "Success", description: "Family member added successfully" });
    },
    onError: (error) => {
      toast({
        title: "Cannot Add Family Member",
        description: resolveAddFamilyMemberToastMessage(error),
        variant: "destructive",
      });
    },
  });

  const archiveFamilyMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      await familyService.archiveMember(memberId, requireFamilyId(familyData?.family?.id));
      return { success: true };
    },
    onSuccess: () => {
      invalidateFamily();
      toast({ title: "Success", description: "Family member archived successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: toastErrorMessage(error, "Failed to archive member"),
        variant: "destructive",
      });
    },
  });

  const deleteFamilyMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      await familyService.removeMember(memberId, requireFamilyId(familyData?.family?.id));
      return { success: true };
    },
    onSuccess: () => {
      invalidateFamily();
      toast({ title: "Success", description: "Family member deleted successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: toastErrorMessage(error, "Failed to delete member"),
        variant: "destructive",
      });
    },
  });

  const updateFamilyMutation = useMutation({
    mutationFn: (updates: { name: string }) =>
      familyService.updateFamilyGroup(requireFamilyId(familyData?.family?.id), {
        name: updates.name,
      }),
    onSuccess: () => {
      invalidateFamily();
      toast({ title: "Success", description: "Family updated successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: toastErrorMessage(error, "Failed to update family"),
        variant: "destructive",
      });
    },
  });

  const deleteFamilyMutation = useMutation({
    mutationFn: async () => {
      await familyService.deleteFamilyGroup(requireFamilyId(familyData?.family?.id));
      return { success: true };
    },
    onSuccess: () => {
      invalidateFamily();
      toast({ title: "Success", description: "Family deleted successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: toastErrorMessage(error, "Failed to delete family"),
        variant: "destructive",
      });
    },
  });

  const updateFamilyMemberMutation = useMutation({
    mutationFn: ({ memberId, updates }: { memberId: string; updates: FamilyMemberUpdate }) =>
      updateFamilyMemberWithOptionalPanEncryption(familyData, userId, memberId, updates),
    onSuccess: () => {
      invalidateFamily();
      toast({ title: "Success", description: "Family member updated successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: toastErrorMessage(error, "Failed to update member"),
        variant: "destructive",
      });
    },
  });

  return {
    createFamilyMutation,
    createFamilyGoalMutation,
    addFamilyMemberMutation,
    archiveFamilyMemberMutation,
    deleteFamilyMemberMutation,
    updateFamilyMutation,
    deleteFamilyMutation,
    updateFamilyMemberMutation,
  };
}
